import { useCallback, useState } from 'react';
import { useApp } from './useApp';
import { useFormErrors } from './useFormStructure';
import useMobileFormValues from './useMobileFormValues';
import { useExtendedFormActions } from './useExtendedFormActions';
import { getResultStructure } from '../utils/mobileFormUtils';
import { IncidentsService } from '../../../services/incidentsService';
import { debounceFunction } from '../../../utility/utility';
import { useNotifications } from '../../../hooks/useNotifications';

const useMobileForm = ({
  idService,
  idApp,
  values,
  saveMobileForm = IncidentsService.saveMobileForm,
  itemType,
  getApplicationForm,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const formData = useApp({ idApp, getApplicationForm });
  const valuesData = useMobileFormValues({
    values,
  });
  const errorsData = useFormErrors();
  const extendedFormActions = useExtendedFormActions({ itemType });
  const { showErrorNotification, showSuccessNotification } = useNotifications();

  const getEvidencesHandler = (newItemId) => async (idInput, value) => {
    return extendedFormActions.uploadEvidences({
      value,
      newItemId,
      idInput,
      onStartUpload: () => setIsUploadingFiles(true),
      onFinishUpload: () => setIsUploadingFiles(false),
      onErrorUploading: () =>
        showErrorNotification({
          title: 'error',
          description: 'errorUploadingFile',
        }),
    });
  };

  const updateFormValues = useCallback(
    (checkErrors) => {
      setIsLoading(true);
      try {
        const newValues = valuesData.getValuesToSave();
        const result = {
          newValues,
        };
        valuesData.updateMobileFormValues(newValues);
        if (checkErrors) {
          result.errors = errorsData.checkFormErrors(formData.dataForm, newValues);
          result.hasError = result.errors?.some((item) => item.errors?.length);
        }
        return result;
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [valuesData.getValuesToSave, valuesData.updateMobileFormValues, errorsData.checkFormErrors, formData.dataForm],
  );

  const getStructure = useCallback(
    async (finish, newItemId) => {
      const { errors, newValues, hasError } = updateFormValues(finish);

      if (hasError) {
        setIsLoading(false);
        return {
          hasValidationErrors: true,
          errors,
          resultStructure: null,
        };
      }

      const resultStructure = await getResultStructure(formData.dataForm, newValues, {
        notifyUploadItems: getEvidencesHandler(newItemId),
      });
      return {
        hasValidationErrors: false,
        errors: null,
        resultStructure,
        newValues,
      };
    },
    [updateFormValues, formData.dataForm],
  );

  const saveMobileFormValues = useCallback(
    debounceFunction(async (finish, itemId) => {
      setIsLoading(true);
      const result = {};

      try {
        const { hasValidationErrors, errors, resultStructure } = await getStructure(finish, itemId);

        if (hasValidationErrors) {
          setIsLoading(false);
          return {
            hasValidationErrors: true,
            errors,
          };
        }

        result.incidentData = await saveMobileForm(idService, resultStructure, finish);

        showSuccessNotification({
          title: 'success',
          description: 'successUpdatingRecord',
        });
      } catch (error) {
        console.error(error);
        result.hasError = true;
        showErrorNotification({
          title: 'error',
          description: 'errorUpdatingRecord',
        });
      } finally {
        setIsLoading(false);
      }
      return result;
    }, 200),
    [idService, getStructure],
  );

  const getFieldList = () => {
    const fieldsList = formData?.dataForm?.flatMap(({ list }) => {
      const items = list ?? [];
      return items;
    });
    return fieldsList ?? [];
  };

  return {
    form: formData,
    errors: errorsData,
    values: {
      ...valuesData,
      updateFormValues,
    },
    saveMobileFormValues,
    getStructure,
    isLoading,
    isUploadingFiles,
    getFieldList,
  };
};

export default useMobileForm;
